import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import Shepherd from "shepherd.js";
import { useNavigate, useLocation } from "react-router-dom";
import { doc, updateDoc } from "firebase/firestore";
import { useTheme } from "@mui/material/styles";
import UserContext from "../assets/user_context";
import { useAuth } from "../services/use-auth";
import { navigateAndWaitForElement } from "../utilities/navigateAndWaitForElement";
import { getTourForPath, getTourById } from "./tourRegistry";
import { useTourAnalytics } from "./useTourAnalytics";

// Import tour manifest so all tours self-register
import "./tours/index";

// --- Contexts ---
// Stable: never triggers re-renders on consumers
const TourActionsContext = createContext(null);
// Volatile: only consumed by HelpButton and similar
const TourStateContext = createContext(null);

export const useTourActions = () => useContext(TourActionsContext);
export const useTourState = () => useContext(TourStateContext);

const TourProvider = ({ children }) => {
  const { user, userData, org, openTrialNotification, subscriptionData } = useContext(UserContext);
  const { db } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const analytics = useTourAnalytics();

  // State
  const [isOnboardingTourActive, setIsOnboardingTourActive] = useState(false);
  const [currentPageTourId, setCurrentPageTourId] = useState(null);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(true);

  // Refs for stale closure prevention
  const tourRef = useRef(null);
  const tourActiveRef = useRef(false);
  const abortControllerRef = useRef(null);
  const autoStartFiredRef = useRef(false);
  const userRef = useRef(user);
  const dbRef = useRef(db);
  const orgRef = useRef(org);
  const navigateRef = useRef(navigate);
  const analyticsRef = useRef(analytics);

  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { dbRef.current = db; }, [db]);
  useEffect(() => { orgRef.current = org; }, [org]);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  useEffect(() => { analyticsRef.current = analytics; }, [analytics]);

  // Check completion status
  useEffect(() => {
    if (userData) {
      const firestoreCompleted = !!userData.completedOnboardingTour;
      const localCompleted = !!localStorage.getItem(`tour_completed_${user?.uid}`);
      setHasCompletedOnboarding(firestoreCompleted || localCompleted);
    }
  }, [userData, user?.uid]);

  // Sync CSS custom properties from MUI theme
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--tour-bg', theme.palette.background.paper);
    root.style.setProperty('--tour-text', theme.palette.text.primary);
    root.style.setProperty('--tour-text-secondary', theme.palette.text.secondary);
    root.style.setProperty('--tour-primary', theme.palette.primary.main);
    root.style.setProperty('--tour-primary-dark', theme.palette.primary.dark);
    root.style.setProperty('--tour-border', theme.palette.divider);
    root.style.setProperty('--tour-error', theme.palette.error.main);

    return () => {
      root.style.removeProperty('--tour-bg');
      root.style.removeProperty('--tour-text');
      root.style.removeProperty('--tour-text-secondary');
      root.style.removeProperty('--tour-primary');
      root.style.removeProperty('--tour-primary-dark');
      root.style.removeProperty('--tour-border');
      root.style.removeProperty('--tour-error');
    };
  }, [theme]);

  // Detect page tour availability based on route
  useEffect(() => {
    const tourConfig = getTourForPath(location.pathname);
    setCurrentPageTourId(tourConfig?.id || null);
  }, [location.pathname]);

  // Cancel tour on org switch
  useEffect(() => {
    if (tourRef.current?.isActive()) {
      tourRef.current.cancel();
    }
  }, [org]);

  // Cancel tour on unexpected route change (browser back button).
  // Skip check if route matches any step's expectedPath (intentional tour navigation).
  useEffect(() => {
    if (!tourRef.current?.isActive()) return;
    const tour = tourRef.current;
    const isExpectedByAnyStep = tour.steps.some(
      step => step.options?.meta?.expectedPath === location.pathname
    );
    if (!isExpectedByAnyStep && location.pathname !== '/') {
      console.log('🎯 Tour: unexpected route change, cancelling');
      tour.cancel();
    }
  }, [location.pathname]);

  // Cleanup on unmount — strip handlers first so the cancel handler
  // doesn't attempt Firestore writes when auth may already be cleared
  useEffect(() => {
    return () => {
      if (tourRef.current) {
        tourRef.current.off('cancel');
        tourRef.current.off('complete');
        tourRef.current.cancel();
        tourRef.current = null;
      }
      abortControllerRef.current?.abort();
    };
  }, []);

  /**
   * Mark onboarding as complete with localStorage fallback.
   */
  const markOnboardingComplete = useCallback(async () => {
    const currentUser = userRef.current;
    const currentDb = dbRef.current;
    if (!currentUser?.uid || !currentDb) return;

    try {
      await updateDoc(doc(currentDb, "users", currentUser.uid), {
        completedOnboardingTour: true,
      });
    } catch (err) {
      console.warn("Tour: failed to write completion to Firestore, using localStorage fallback", err);
      localStorage.setItem(`tour_completed_${currentUser.uid}`, 'true');
      // Retry once after 2 seconds
      setTimeout(async () => {
        try {
          await updateDoc(doc(currentDb, "users", currentUser.uid), {
            completedOnboardingTour: true,
          });
          localStorage.removeItem(`tour_completed_${currentUser.uid}`);
        } catch {
          // give up, localStorage covers it
        }
      }, 2000);
    }
    setHasCompletedOnboarding(true);
  }, []);

  /**
   * Create and start a Shepherd tour.
   * Does NOT touch abortControllerRef — caller manages that.
   */
  const createAndStartTour = useCallback((steps, options = {}) => {
    const { isOnboarding = false } = options;

    // Clean up existing tour — strip handlers first so the old cancel
    // handler doesn't abort the new session's AbortController
    if (tourRef.current) {
      tourRef.current.off('cancel');
      tourRef.current.off('complete');
      tourRef.current.cancel();
      tourRef.current = null;
    }

    console.log(`🎯 Tour: creating ${isOnboarding ? 'onboarding' : 'page'} tour with ${steps.length} steps`);

    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: 't3-shepherd-step',
        scrollTo: { behavior: 'smooth', block: 'center' },
        cancelIcon: { enabled: true },
        modalOverlayOpeningPadding: 8,
        modalOverlayOpeningRadius: 8,
      },
      exitOnEsc: true,
      keyboardNavigation: true,
    });

    // Add steps
    steps.forEach(step => tour.addStep(step));

    // Event handlers
    if (isOnboarding) {
      tour.on('start', () => {
        console.log('🎯 Tour: onboarding started');
        analyticsRef.current.trackStarted();
      });

      tour.on('show', () => {
        const currentStep = tour.getCurrentStep();
        if (currentStep) {
          const stepIndex = tour.steps.indexOf(currentStep);
          console.log(`🎯 Tour: showing step ${stepIndex + 1}/${tour.steps.length} — ${currentStep.id}`);
          analyticsRef.current.trackStepViewed(currentStep.id, stepIndex);
        }
      });
    }

    tour.on('complete', () => {
      console.log('🎯 Tour: completed');
      tourActiveRef.current = false;
      if (isOnboarding) {
        setIsOnboardingTourActive(false);
        markOnboardingComplete();
        analyticsRef.current.trackCompleted();
      }
    });

    tour.on('cancel', () => {
      console.log('🎯 Tour: cancelled');
      tourActiveRef.current = false;
      abortControllerRef.current?.abort();
      if (isOnboarding) {
        setIsOnboardingTourActive(false);
        markOnboardingComplete();
        const currentStep = tour.getCurrentStep();
        const stepIndex = tour.steps.indexOf(currentStep);
        // Pass orgId so the Cloud Function can create an org-scoped notification
        analyticsRef.current.trackDismissed(currentStep?.id, stepIndex, orgRef.current);
      }
    });

    tourRef.current = tour;
    tour.start();
  }, [markOnboardingComplete]);

  /**
   * Start the onboarding tour.
   */
  const startOnboardingTour = useCallback(async () => {
    // Mobile guard
    if (window.innerWidth < 600) {
      console.log('🎯 Tour: skipped — window too narrow for tour');
      return;
    }
    // Double-click guard
    if (tourActiveRef.current) {
      console.log('🎯 Tour: skipped — tour already active');
      return;
    }
    tourActiveRef.current = true;
    setIsOnboardingTourActive(true);

    // Create a fresh AbortController for this tour session
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const currentNavigate = navigateRef.current;

    // Helper for tour steps to navigate and wait.
    // Reads signal from the ref at call time so it's always current.
    const navigateAndWait = (path, selector) =>
      navigateAndWaitForElement(currentNavigate, path, selector, {
        signal: abortControllerRef.current?.signal,
      });

    try {
      // Get onboarding steps from registry
      const onboardingConfig = getTourById('onboarding');
      if (!onboardingConfig) {
        console.error("🎯 Tour: onboarding tour not registered");
        tourActiveRef.current = false;
        setIsOnboardingTourActive(false);
        return;
      }

      const steps = onboardingConfig.getSteps({ navigateAndWait });

      console.log('🎯 Tour: navigating to dashboard...');
      // Navigate to dashboard first and wait for welcome element
      await navigateAndWait('/', '[data-tour="dashboard-welcome"]');
      console.log('🎯 Tour: dashboard ready, starting tour');

      createAndStartTour(steps, { isOnboarding: true });
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error("🎯 Tour: failed to start onboarding", err);
      }
      tourActiveRef.current = false;
      setIsOnboardingTourActive(false);
    }
  }, [createAndStartTour]);

  /**
   * Start a per-page tour by ID.
   */
  const startPageTour = useCallback((tourId) => {
    // Mobile guard
    if (window.innerWidth < 600) return;
    // Double-click guard
    if (tourActiveRef.current) return;
    tourActiveRef.current = true;

    // Create a fresh AbortController for this tour session
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    const config = getTourById(tourId);
    if (!config) {
      console.error(`🎯 Tour: tour "${tourId}" not registered`);
      tourActiveRef.current = false;
      return;
    }

    const steps = config.getSteps({});
    createAndStartTour(steps, { isOnboarding: false });
  }, [createAndStartTour]);

  // Auto-start onboarding tour for new users who haven't completed it.
  // Guards ensure we wait for:
  //  - subscription data to load (so the trial modal has a chance to show first)
  //  - the trial notification modal to be dismissed (don't overlay tour on modal)
  // The ref is set INSIDE the timeout so dependency changes can restart the timer.
  useEffect(() => {
    if (
      autoStartFiredRef.current ||
      hasCompletedOnboarding ||
      !org ||
      !userData ||
      !subscriptionData ||       // wait for subscription data before starting
      openTrialNotification ||   // trial modal is showing — wait for dismiss
      tourActiveRef.current
    ) return;

    const timerId = setTimeout(() => {
      autoStartFiredRef.current = true;
      console.log('🎯 Tour: auto-starting for new user');
      startOnboardingTour();
    }, 1500);

    return () => clearTimeout(timerId);
  }, [hasCompletedOnboarding, org, userData, subscriptionData, openTrialNotification, startOnboardingTour]);

  // --- Context values ---
  const actions = useMemo(() => ({
    startOnboardingTour,
    startPageTour,
  }), [startOnboardingTour, startPageTour]);

  const state = useMemo(() => ({
    isOnboardingTourActive,
    currentPageTourId,
    hasCompletedOnboarding,
  }), [isOnboardingTourActive, currentPageTourId, hasCompletedOnboarding]);

  return (
    <TourActionsContext.Provider value={actions}>
      <TourStateContext.Provider value={state}>
        {children}
      </TourStateContext.Provider>
    </TourActionsContext.Provider>
  );
};

export default TourProvider;
